import React from 'react';
import './question-feedback.css';

/**
 * QuestionFeedback — shared Question Evaluator result (REQ-005, FE-5-1)
 * Location: frontend/src/components/question-evaluator/QuestionFeedback.js
 *
 * Renders a QuestionEvalResult (CONTRACTS.md → REQ-001 / REQ-005): verdict,
 * per-criterion checklist, reshaped suggestion and the optional "switch type"
 * hint. Used by the Question Evaluator page and the Report Writer's Evaluate
 * Report reply. Every string is rendered as plain text (SEC-1-1).
 *
 * The "Evaluate it as … instead" button renders only when `onSwitchType` is
 * passed; without it the hint is text only (Report Writer, D-5). The reshape
 * never has a button (D-4).
 */

// Type metadata. `value` matches the API contract enum.
const QUESTION_TYPES = {
  key_decision: {
    value: 'key_decision',
    label: 'Key Decision',
    other: 'knowledge_gap',
    otherLabel: 'Knowledge Gap',
    rewriteLabel: 'Polished Key Decision',
  },
  knowledge_gap: {
    value: 'knowledge_gap',
    label: 'Knowledge Gap',
    other: 'key_decision',
    otherLabel: 'Key Decision',
    rewriteLabel: 'Reframed as a Knowledge Gap',
  },
};

const QuestionFeedback = ({ result, type, onSwitchType }) => {
  if (!result) return null;

  const meta = QUESTION_TYPES[type] || QUESTION_TYPES.key_decision;
  const criteria = result.criteria || [];

  return (
    <div className="question-feedback">
      <div className={`verdict ${result.fits ? 'good' : 'warn'}`}>
        <span className="ico">{result.fits ? '✓' : '◑'}</span>
        <span>{result.verdict}</span>
      </div>

      <ul className="check-list">
        {criteria.map((c, i) => (
          <li key={c.label || i}>
            <span className={`mark ${c.met ? 'yes' : 'no'}`}>{c.met ? '✓' : '!'}</span>
            <span className="txt">
              <strong>{c.label}</strong> — <span>{c.note}</span>
            </span>
          </li>
        ))}
      </ul>

      {result.reshape && (
        <div className="rewrite">
          <span className="label">{meta.rewriteLabel}</span>
          {result.reshape}
        </div>
      )}

      {result.switch_suggestion && (
        <div className="switch-hint">
          {result.switch_suggestion}
          {onSwitchType && (
            <>
              {' '}
              <button
                type="button"
                className="link"
                onClick={() => onSwitchType(meta.other)}
              >
                Evaluate it as a {meta.otherLabel} instead →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionFeedback;
